import { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    TextField,
    Stack
} from '@mui/material';
import {
    Visibility as VisibilityIcon,
    CheckCircle as ApproveIcon,
    Cancel as RejectIcon,
    Delete as DeleteIcon,
    Download as DownloadIcon,
    AttachFile as AttachFileIcon
} from '@mui/icons-material';
import { getClientPrincipal } from '../services/auth';
import {
    fetchAllBusinessCases,
    approveBusinessCase,
    rejectBusinessCase,
    deleteBusinessCase,
    copyAttachmentToSharePoint,
    BusinessCaseClient
} from '../services/businessCase';
import {
    sendBusinessCaseApproval,
    sendBusinessCaseRejection
} from '../services/email';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            {...other}
        >
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
}

const BusinessCaseAdminPage = () => {
    const [businessCases, setBusinessCases] = useState<BusinessCaseClient[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedCase, setSelectedCase] = useState<BusinessCaseClient | null>(null);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [tabValue, setTabValue] = useState(0);
    const [processing, setProcessing] = useState(false);
    const [userEmail, setUserEmail] = useState<string | null>(null);
    const [accessDenied, setAccessDenied] = useState(false);

    // Authorized users for Business Case Admin
    const BUSINESS_CASE_ADMIN_EMAILS = [
        'si@iodparc.com',
        'joanne@iodparc.com',
        'jonny@iodparc.com',
        'giulia@iodparc.com'
    ];

    useEffect(() => {
        checkAccess();
    }, []);

    const checkAccess = async () => {
        try {
            const principal = await getClientPrincipal();
            const email = principal?.userDetails?.toLowerCase() || null;
            setUserEmail(email);

            if (!email || !BUSINESS_CASE_ADMIN_EMAILS.includes(email)) {
                setAccessDenied(true);
                setLoading(false);
                return;
            }

            loadBusinessCases();
        } catch (err) {
            console.error('Error checking access:', err);
            setAccessDenied(true);
            setLoading(false);
        }
    };

    const loadBusinessCases = async () => {
        try {
            setLoading(true);
            const cases = await fetchAllBusinessCases();
            setBusinessCases(cases);
        } catch (err) {
            console.error('Error loading business cases:', err);
            setError('Failed to load business cases');
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = (businessCase: BusinessCaseClient) => {
        setSelectedCase(businessCase);
        setDetailsDialogOpen(true);
    };

    const handleApprove = async (businessCase: BusinessCaseClient) => {
        try {
            setProcessing(true);
            const principal = await getClientPrincipal();
            if (!principal?.name) {
                throw new Error('User not authenticated');
            }

            const approvedCase = await approveBusinessCase(businessCase.id!, principal.name);

            // Process approval: generate PDF and copy attachment to SharePoint (if present)
            const sharePointResult = await copyAttachmentToSharePoint({
                ...businessCase,
                approval_date: approvedCase.approval_date
            });
            if (!sharePointResult.success) {
                console.warn('Failed to process business case approval files:', sharePointResult.error);
                alert('Business case approved, but failed to generate PDF / copy attachment to SharePoint. Please upload manually.');
            }

            // Send approval email
            await sendBusinessCaseApproval({
                owner_email: businessCase.owner_email,
                owner_name: businessCase.owner_name,
                opportunity_name: businessCase.opportunity_name,
                client: businessCase.client,
                approved_by: principal.name
            });

            // Reload cases
            await loadBusinessCases();
            setDetailsDialogOpen(false);
        } catch (err) {
            console.error('Error approving business case:', err);
            alert('Failed to approve business case');
        } finally {
            setProcessing(false);
        }
    };

    const handleRejectClick = (businessCase: BusinessCaseClient) => {
        setSelectedCase(businessCase);
        setRejectionReason('');
        setRejectDialogOpen(true);
    };

    const handleRejectConfirm = async () => {
        if (!selectedCase || !rejectionReason.trim()) {
            alert('Please provide a rejection reason');
            return;
        }

        try {
            setProcessing(true);
            await rejectBusinessCase(selectedCase.id!, rejectionReason);

            // Send rejection email
            await sendBusinessCaseRejection({
                owner_email: selectedCase.owner_email,
                owner_name: selectedCase.owner_name,
                opportunity_name: selectedCase.opportunity_name,
                client: selectedCase.client,
                rejection_reason: rejectionReason
            });

            // Reload cases
            await loadBusinessCases();
            setRejectDialogOpen(false);
            setDetailsDialogOpen(false);
        } catch (err) {
            console.error('Error rejecting business case:', err);
            alert('Failed to reject business case');
        } finally {
            setProcessing(false);
        }
    };

    const handleDeleteClick = (businessCase: BusinessCaseClient) => {
        setSelectedCase(businessCase);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedCase) return;

        try {
            setProcessing(true);
            await deleteBusinessCase(selectedCase.id!);
            
            await loadBusinessCases();
            setDeleteDialogOpen(false);
            setDetailsDialogOpen(false);
        } catch (err) {
            console.error('Error deleting business case:', err);
            alert('Failed to delete business case');
        } finally {
            setProcessing(false);
        }
    };

    const getStatusColor = (status: string): "default" | "warning" | "success" | "error" => {
        switch (status) {
            case 'pending':
                return 'warning';
            case 'approved':
                return 'success';
            case 'rejected':
                return 'error';
            default:
                return 'default';
        }
    };

    const pendingCases = businessCases.filter(bc => bc.status === 'pending');
    const pastCases = businessCases.filter(bc => bc.status !== 'pending');

    if (accessDenied) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Paper elevation={3} sx={{ p: 3 }}>
                    <Alert severity="error" sx={{ mb: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Access Denied
                        </Typography>
                        <Typography>
                            You do not have permission to access the Business Case Admin page.
                            This page is restricted to authorized personnel only.
                        </Typography>
                        {userEmail && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Current user: {userEmail}
                            </Typography>
                        )}
                    </Alert>
                </Paper>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom sx={{ color: '#009688', mb: 3 }}>
                    Business Case Admin
                </Typography>

                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                        <Tab label={`Pending (${pendingCases.length})`} />
                        <Tab label={`Past Cases (${pastCases.length})`} />
                    </Tabs>
                </Box>

                {/* Pending Cases Tab */}
                <TabPanel value={tabValue} index={0}>
                    {pendingCases.length === 0 ? (
                        <Alert severity="info">No pending business cases</Alert>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Opportunity Name</TableCell>
                                        <TableCell>Client</TableCell>
                                        <TableCell>Submitted By</TableCell>
                                        <TableCell>Submitted</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pendingCases.map((bc) => (
                                        <TableRow key={bc.id}>
                                            <TableCell>{bc.opportunity_name}</TableCell>
                                            <TableCell>{bc.client}</TableCell>
                                            <TableCell>{bc.owner_name}</TableCell>
                                            <TableCell>
                                                {bc.submission_date ? new Date(bc.submission_date).toLocaleDateString() : 'N/A'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={bc.status.toUpperCase()}
                                                    color={getStatusColor(bc.status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleViewDetails(bc)}
                                                    title="View Details"
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="success"
                                                    onClick={() => handleApprove(bc)}
                                                    title="Approve"
                                                    disabled={processing}
                                                >
                                                    <ApproveIcon />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleRejectClick(bc)}
                                                    title="Reject"
                                                    disabled={processing}
                                                >
                                                    <RejectIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </TabPanel>

                {/* Past Cases Tab */}
                <TabPanel value={tabValue} index={1}>
                    {pastCases.length === 0 ? (
                        <Alert severity="info">No past business cases</Alert>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Opportunity Name</TableCell>
                                        <TableCell>Client</TableCell>
                                        <TableCell>Submitted By</TableCell>
                                        <TableCell>Submitted</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Decision By</TableCell>
                                        <TableCell align="right" sx={{ position: 'sticky', right: 0, bgcolor: 'background.paper', zIndex: 2 }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pastCases.map((bc) => (
                                        <TableRow key={bc.id}>
                                            <TableCell>{bc.opportunity_name}</TableCell>
                                            <TableCell>{bc.client}</TableCell>
                                            <TableCell>{bc.owner_name}</TableCell>
                                            <TableCell>
                                                {bc.submission_date ? new Date(bc.submission_date).toLocaleDateString() : 'N/A'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={bc.status.toUpperCase()}
                                                    color={getStatusColor(bc.status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{bc.approved_by || 'N/A'}</TableCell>
                                            <TableCell align="right" sx={{ position: 'sticky', right: 0, bgcolor: 'background.paper' }}>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                    startIcon={<VisibilityIcon />}
                                                    onClick={() => handleViewDetails(bc)}
                                                    title="View full business case"
                                                    sx={{ mr: 1 }}
                                                >
                                                    View
                                                </Button>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteClick(bc)}
                                                    title="Delete"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </TabPanel>
            </Paper>

            {/* Details Dialog */}
            <Dialog
                open={detailsDialogOpen}
                onClose={() => setDetailsDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Business Case Details</DialogTitle>
                <DialogContent>
                    {selectedCase && (
                        <Box sx={{ pt: 2 }}>
                            <Typography variant="h6" gutterBottom>{selectedCase.opportunity_name}</Typography>
                            
                            <Stack spacing={2}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">Submitted By</Typography>
                                    <Typography variant="body1">{selectedCase.owner_name} ({selectedCase.owner_email})</Typography>
                                </Box>

                                <Box>
                                    <Typography variant="body2" color="text.secondary">Client</Typography>
                                    <Typography variant="body1">{selectedCase.client}</Typography>
                                </Box>

                                <Box>
                                    <Typography variant="body2" color="text.secondary">Proposal Coordinator</Typography>
                                    <Typography variant="body1">{selectedCase.proposal_coordinator}</Typography>
                                </Box>

                                {selectedCase.country_region && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Country/Region</Typography>
                                        <Typography variant="body1">{selectedCase.country_region}</Typography>
                                    </Box>
                                )}

                                {selectedCase.budget && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Budget</Typography>
                                        <Typography variant="body1">{selectedCase.budget}</Typography>
                                    </Box>
                                )}

                                {selectedCase.deadline && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Deadline</Typography>
                                        <Typography variant="body1">
                                            {new Date(selectedCase.deadline).toLocaleDateString()}
                                        </Typography>
                                    </Box>
                                )}

                                {selectedCase.workstream && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Workstream</Typography>
                                        <Typography variant="body1">{selectedCase.workstream}</Typography>
                                    </Box>
                                )}

                                {selectedCase.strategic_case && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Strategic Case</Typography>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {selectedCase.strategic_case}
                                        </Typography>
                                    </Box>
                                )}

                                {selectedCase.competitive_advantage && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Competitive Advantage</Typography>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {selectedCase.competitive_advantage}
                                        </Typography>
                                    </Box>
                                )}

                                {selectedCase.corporate_experience && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Corporate Experience</Typography>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {selectedCase.corporate_experience}
                                        </Typography>
                                    </Box>
                                )}

                                {selectedCase.prospective_team_configuration && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Prospective Team Configuration</Typography>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {selectedCase.prospective_team_configuration}
                                        </Typography>
                                    </Box>
                                )}

                                {selectedCase.bidding_capacity && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Bidding Capacity</Typography>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {selectedCase.bidding_capacity}
                                        </Typography>
                                    </Box>
                                )}

                                {selectedCase.partnership && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Partnership</Typography>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {selectedCase.partnership}
                                        </Typography>
                                    </Box>
                                )}

                                {selectedCase.commercial_case && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Commercial Case</Typography>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {selectedCase.commercial_case}
                                        </Typography>
                                    </Box>
                                )}

                                {selectedCase.other_risks && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Other Risks</Typography>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {selectedCase.other_risks}
                                        </Typography>
                                    </Box>
                                )}

                                {selectedCase.conflict_of_interest && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Conflicts of Interest</Typography>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {selectedCase.conflict_of_interest}
                                        </Typography>
                                    </Box>
                                )}

                                {selectedCase.learning_development && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Learning & Development</Typography>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {selectedCase.learning_development}
                                        </Typography>
                                    </Box>
                                )}

                                {selectedCase.gender_equality_social_inclusion && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Gender Equality & Social Inclusion</Typography>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {selectedCase.gender_equality_social_inclusion}
                                        </Typography>
                                    </Box>
                                )}

                                {selectedCase.attachment_filename && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Attachment</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                            <AttachFileIcon fontSize="small" color="primary" />
                                            <Typography variant="body1">{selectedCase.attachment_filename}</Typography>
                                            {selectedCase.attachment_size && (
                                                <Typography variant="body2" color="text.secondary">
                                                    ({(selectedCase.attachment_size / 1024).toFixed(1)} KB)
                                                </Typography>
                                            )}
                                            {selectedCase.attachment_data && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<DownloadIcon />}
                                                    onClick={() => {
                                                        // Download the attachment
                                                        const link = document.createElement('a');
                                                        link.href = selectedCase.attachment_data!;
                                                        link.download = selectedCase.attachment_filename!;
                                                        document.body.appendChild(link);
                                                        link.click();
                                                        document.body.removeChild(link);
                                                    }}
                                                >
                                                    Download
                                                </Button>
                                            )}
                                            {!selectedCase.attachment_data && (
                                                <Typography variant="body2" color="error">
                                                    (File not available)
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                )}

                                {selectedCase.rejection_reason && (
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Rejection Reason</Typography>
                                        <Alert severity="warning">{selectedCase.rejection_reason}</Alert>
                                    </Box>
                                )}
                            </Stack>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
                    {selectedCase && selectedCase.status === 'pending' && (
                        <>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<ApproveIcon />}
                                onClick={() => handleApprove(selectedCase)}
                                disabled={processing}
                            >
                                Approve
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<RejectIcon />}
                                onClick={() => {
                                    setDetailsDialogOpen(false);
                                    handleRejectClick(selectedCase);
                                }}
                                disabled={processing}
                            >
                                Reject
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* Rejection Dialog */}
            <Dialog
                open={rejectDialogOpen}
                onClose={() => setRejectDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Reject Business Case</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" gutterBottom>
                        Please provide a reason for rejecting this business case:
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder="Enter rejection reason..."
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleRejectConfirm}
                        disabled={!rejectionReason.trim() || processing}
                    >
                        Confirm Rejection
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Business Case</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this business case? This action cannot be undone.
                    </Typography>
                    {selectedCase && (
                        <Typography variant="body2" sx={{ mt: 2, fontWeight: 'bold' }}>
                            {selectedCase.opportunity_name}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        disabled={processing}
                    >
                        {processing ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default BusinessCaseAdminPage;
